import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import OtpModal from './OtpModal';
import PasswordModal from './PasswordModal';
import { BASE_URL } from '../../utils/constants';


const Signup: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Get parameters from URL
  const emailFromUrl = searchParams.get('email') || '';
  const slugFromUrl = searchParams.get('slug') || '';
  const tokenFromUrl = searchParams.get('token') || '';

  const [email, setEmail] = useState(emailFromUrl);
  const [tenantName, setTenantName] = useState('Acme Corp');
  const [tenantSlug, setTenantSlug] = useState(slugFromUrl || 'acme-test-corp-slug');

  const [sessionId, setSessionId] = useState<string>('');
  const [otpModalOpen, setOtpModalOpen] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);

  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [timer, setTimer] = useState(600);
  const [resendDisabled, setResendDisabled] = useState(true);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (tenantName && tenantName !== 'Acme Corp' && !slugFromUrl) {
      const generated = tenantName
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      setTenantSlug(generated);
    }
  }, [tenantName, slugFromUrl]);

  // OTP Timer
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    if (otpModalOpen && timer > 0) {
      interval = setInterval(() => setTimer((prev) => prev - 1), 1000);
    } else if (timer === 0) {
      setResendDisabled(false);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [otpModalOpen, timer]);

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !tenantName || !tenantSlug) {
      toast.error('All fields are required');
      return;
    }

    setLoading(true);

    try {
      const requestBody: any = {
        email: email.trim(),
        tenantSlug: tenantSlug.trim(),
        tenantName: tenantName.trim(),
      };

      if (tokenFromUrl) {
        requestBody.inviteToken = tokenFromUrl;
      }

      const response = await fetch(`${BASE_URL}/v1/auth/signup/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();

      if (response.ok) {
        setSessionId(data.sessionId);
        setOtp(['', '', '', '', '', '']);
        setOtpModalOpen(true);
        setTimer(data.otpExpiresInSeconds || 600);
        toast.success('Verification code sent!');
      } else {
        toast.error(data.message || 'Failed to start signup');
      }
    } catch (err) {
      toast.error('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpSubmit = async () => {
    const enteredOtp = otp.join('');
    if (enteredOtp.length !== 6) return;

    setLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/v1/auth/signup/verify-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId, otp: enteredOtp }),
      });

      if (res.status === 204) {
        setOtpModalOpen(false);
        setPasswordModalOpen(true);
        toast.success('Email verified successfully!');
      } else {
        toast.error('Invalid or expired OTP');
      }
    } catch (err) {
      toast.error('Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (values: { password: string }) => {
    setLoading(true);
    try {
      const requestBody: any = {
        sessionId,
        password: values.password,
      };

      if (tokenFromUrl) {
        requestBody.inviteToken = tokenFromUrl;
      }

      const res = await fetch(`${BASE_URL}/v1/auth/signup/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      });
      // Might need this code below later, so keeping it commented for now.

      // const data = await res.json();

      //       if (res.ok && data.nextAuthorizeUrl) {
      //         toast.success('Signup complete! Redirecting...');

      //         let redirectUrl = data.nextAuthorizeUrl;

      //         // =============================================================
      //         // IMPORTANT: LOCAL DEVELOPMENT REDIRECT OVERRIDE
      //         // Change this to production URL after deploying frontend
      //         // =============================================================
      //         try {
      //           const url = new URL(redirectUrl);
      //           const params = new URLSearchParams(url.search);

      //           // TODO: Replace localhost with production callback after deployment
      //           params.set('redirect_uri', 'http://localhost:3000/callback');

      //           url.search = params.toString();
      //           redirectUrl = url.toString();
      //         } catch (e) {
      //           console.error('Failed to modify redirect URL:', e);
      //         }
      //         // =============================================================

      //         window.location.href = redirectUrl;
      //       } else {
      //         toast.error('Failed to complete signup');
      //       }
      if (res.ok) {
        toast.success('Signup successful! Please login with your credentials.', {
          autoClose: 4000,
        });

        setEmail('');
        setPasswordModalOpen(false);

        setTimeout(() => {
          navigate('/login');
        }, 1500);
      } else {
        toast.error('Failed to complete signup');
      }
    } catch (err) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = email.trim() && tenantName.trim() && tenantSlug.trim();

  return (
    <>
      <form onSubmit={handleSignup} className="space-y-5">
        <div>
          <label className="block text-sm font-medium mb-1" style={{ color: '#9FA9B2', fontSize: '12px' }}>
            EMAIL <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 ${emailFromUrl ? 'bg-gray-50' : ''
              }`}
            placeholder="Enter email address"
            required
            readOnly={!!emailFromUrl}
          />
          {emailFromUrl && (
            <p className="text-xs text-green-600 mt-1">
              ✓ Email pre-filled from your invitation
            </p>
          )}
          {tokenFromUrl && (
            <p className="text-xs text-blue-600 mt-1">
              🔑 Invitation token validated
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium mb-1" style={{ color: '#9FA9B2', fontSize: '12px' }}>
            ORGANIZATION NAME <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={tenantName}
            onChange={(e) => setTenantName(e.target.value)}
            className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
            placeholder="Acme Corp"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1" style={{ color: '#9FA9B2', fontSize: '12px' }}>
            ORGANIZATION SLUG <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={tenantSlug}
            onChange={(e) => setTenantSlug(e.target.value)}
            className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
            placeholder="acme-test-corp-slug"
            required
            readOnly={!!slugFromUrl}
          />
          {slugFromUrl && (
            <p className="text-xs text-green-600 mt-1">
              ✓ Organization slug pre-filled from your invitation
            </p>
          )}
        </div>

        <button
          type="submit"
          disabled={loading || !isFormValid}
          className="w-full text-white py-3 rounded-lg font-medium transition-colors"
          style={{
            backgroundColor: isFormValid && !loading ? '#FF7A59' : '#C4CBD3',
          }}
        >
          {loading ? 'Processing...' : 'Continue'}
        </button>
      </form>

      <p className="mt-6 text-center text-sm" style={{ color: '#3F5265' }}>
        Already have an account?{' '}
        <button
          onClick={() => navigate('/login')}
          className="text-blue-500 hover:underline"
          style={{ color: '#00A4BD' }}
        >
          Sign In
        </button>
      </p>

      {otpModalOpen && (
        <OtpModal
          email={email}
          otp={otp}
          timer={timer}
          resendDisabled={resendDisabled}
          isOtpValid={otp.every((digit) => digit.length === 1)}
          onOtpChange={(index, value) => {
            const newOtp = [...otp];
            newOtp[index] = value;
            setOtp(newOtp);
          }}
          onOtpKeyDown={() => { }}
          onResend={() => { }}
          onContinue={handleOtpSubmit}
          onClose={() => setOtpModalOpen(false)}
        />
      )}

      {passwordModalOpen && (
        <PasswordModal
          onSubmit={handlePasswordSubmit}
          onClose={() => setPasswordModalOpen(false)}
        />
      )}

      <ToastContainer position="top-right" autoClose={4000} />
    </>
  );
};

export default Signup;